import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { isEmpty, isNil, noop } from 'lodash/fp';
import { useTranslation } from 'react-i18next';
import { ComboBox } from 'app/components/core/ComboBox';
import * as styles from './SlackChannelSelect.css';

const cx = classnames.bind(styles);

const CHANNEL_NAME_PATTERN = /^[a-z0-9_-]+$/;
const MAX_NAME_LENGTH = 80;

export function SlackChannelSelect({
    value = null,
    isLoading = false,
    serverError = null,
    channels = null,
    onListSlackChannels = noop,
    onCreate = noop,
    onChange = noop,
    ...props
}) {
    const { t } = useTranslation();

    const [channelName, setChannelName] = useState(null);
    const [validationError, setValidationError] = useState(null);

    useEffect(() => {
        if (!channels) {
            onListSlackChannels();
        }

        if (value && channels) {
            const channel = channels.find((e) => e.id === value);
            setChannelName(channel?.name);
        }
    }, [value, channels, onListSlackChannels]);

    const error = serverError || validationError;
    const classes = cx('comboBox', { hasError: !isNil(error) });
    const options = channels ? channels.map((channel) => ({ name: channel.name })) : [];

    const validateChannelName = (name) => {
        if (!CHANNEL_NAME_PATTERN.test(name)) {
            return t('slack:channelValidationError.invalid');
        }

        if (name.length > MAX_NAME_LENGTH) {
            return t('slack:channelValidationError.tooLong');
        }

        return null;
    };

    const handleChannelSelection = ({ name }) => {
        if (isLoading || isEmpty(name) || name === channelName) {
            return;
        }

        const validationError = validateChannelName(name);
        setChannelName(name);
        setValidationError(validationError);

        if (isNil(validationError)) {
            const channel = channels?.find((e) => e.name === name);
            if (channel !== undefined) {
                onChange(channel.id);
            } else {
                onCreate(name, onChange);
            }
        }
    };

    return (
        <ComboBox
            name={name}
            className={classes}
            error={validationError}
            value={channelName && { name: channelName }}
            placeholder={t('slack:selectOrCreateChannel')}
            label={t('slack:channel')}
            options={options}
            onChange={handleChannelSelection}
            isProcessing={isLoading}
            allowCustomValue
            {...props}
        />
    );
}

SlackChannelSelect.propTypes = {
    value: PropTypes.string,
    serverError: PropTypes.shape({}),
    channels: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string
        })
    ),
    isLoading: PropTypes.bool,
    onListSlackChannels: PropTypes.func,
    onCreate: PropTypes.func,
    onChange: PropTypes.func
};
